import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shield, User, Calendar, Hash, Building } from "@/components/icons";
import { changeRole, deleteUser, fetchUserAccess, fetchUserResetAvailability, grantPrime, revokePrime, setUserContact, sendUserReset, setUserDisabled, unlockUser, type ResetChannel, type UserRecord } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useResetPassword } from "@/components/ResetPasswordAction";
import { formatDuration } from "@/lib/utils";

interface UserDetailPanelProps {
  user: UserRecord;
  isSelf: boolean;
  onDeleted?: () => void;
}

export function UserDetailPanel({ user: u, isSelf, onDeleted }: UserDetailPanelProps) {
  const qc = useQueryClient();

  // After any mutation, refresh BOTH places this panel is rendered from:
  // the Users list query (slide-panel path, kept in sync via allUsers) AND the
  // single-user query behind the standalone /users/:id page. Without the
  // latter, an action on the detail page (e.g. granting Prime) succeeds on the
  // server but the panel keeps showing stale data until a manual reload.
  const invalidateUser = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ["users"] });
    qc.invalidateQueries({ queryKey: ["user", u.id] });
  }, [qc, u.id]);

  // Temporary free-Prime grant (admin comp). Number of days from now.
  const [primeDays, setPrimeDays] = useState("30");

  // Contact editing
  const [editContact, setEditContact] = useState(false);
  const [emailInput, setEmailInput] = useState(u.email ?? "");
  const [phoneInput, setPhoneInput] = useState(u.phone ?? "");
  React.useEffect(() => {
    // Re-sync the inputs when the selected user refreshes underneath us.
    setEmailInput(u.email ?? "");
    setPhoneInput(u.phone ?? "");
  }, [u.id, u.email, u.phone]);

  // What this user can reach: org memberships + directly assigned units —
  // the user-side mirror of the unit page's "assigned users" list.
  const accessQ = useQuery({
    queryKey: ["user-access", u.id],
    queryFn: () => fetchUserAccess(u.id),
  });

  const roleM = useMutation({
    mutationFn: () => changeRole(u.id, u.role === "admin" ? "user" : "admin"),
    onSuccess: () => { toast.success("Role updated"); invalidateUser(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const deleteM = useMutation({
    mutationFn: () => deleteUser(u.id),
    onSuccess: () => { toast.success("User deleted"); invalidateUser(); onDeleted?.(); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const disableM = useMutation({
    mutationFn: () => setUserDisabled(u.id, !u.disabled),
    onSuccess: () => {
      toast.success(u.disabled ? "User enabled" : "User disabled");
      invalidateUser();
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const unlockM = useMutation({
    mutationFn: () => unlockUser(u.id),
    onSuccess: () => {
      toast.success("Lockout cleared");
      invalidateUser();
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const grantPrimeM = useMutation({
    mutationFn: (days: number) => grantPrime(u.id, days),
    onSuccess: () => {
      toast.success("Free Prime granted");
      invalidateUser();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to grant Prime"),
  });

  const revokePrimeM = useMutation({
    mutationFn: () => revokePrime(u.id),
    onSuccess: () => {
      toast.success("Free Prime revoked");
      invalidateUser();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to revoke Prime"),
  });

  const contactM = useMutation({
    mutationFn: () =>
      setUserContact(u.id, { email: emailInput.trim(), phone: phoneInput.trim() }),
    onSuccess: () => {
      toast.success("Contact updated");
      setEditContact(false);
      invalidateUser();
    },
    onError: (e: any) =>
      toast.error(
        e?.message?.includes("email_taken")
          ? "That email is already used by another account."
          : e?.message || "Failed to update contact",
      ),
  });

  // One "Reset password" action: email+phone → masked channel dialog, one method
  // → direct, neither → disabled. The admin never sees the OTP/token/password.
  async function initiateReset(channel: ResetChannel) {
    try {
      await sendUserReset(u.id, channel);
      toast.success("Password reset has been requested");
    } catch (e: any) {
      toast.error(e?.body?.message || e?.message || "Failed to request password reset");
    }
  }
  // Availability + masked destinations come from the backend (authoritative:
  // email present; phone verified + valid E.164 + allowed country).
  const availabilityQ = useQuery({
    queryKey: ["reset-availability", "user", u.id],
    queryFn: () => fetchUserResetAvailability(u.id),
  });
  const avail = availabilityQ.data;
  const reset = useResetPassword({
    emailAvailable: avail?.email_reset_available ?? false,
    smsAvailable: avail?.sms_reset_available ?? false,
    maskedEmail: avail?.masked_email,
    maskedPhone: avail?.masked_phone,
    loading: availabilityQ.isLoading,
    error: availabilityQ.isError,
    initiate: initiateReset,
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          {u.role === "admin" ? <Shield className="h-5 w-5 text-primary" /> : <User className="h-5 w-5 opacity-60" />}
          <h2 className="text-xl font-bold">{u.username}</h2>
          {isSelf && <Badge variant="info" className="text-[11px] h-4.5 px-1.5">You</Badge>}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Badge variant={u.role === "admin" ? "primary" : "neutral"}>{u.role}</Badge>
          {u.tier && u.tier !== "free" && (
            <Badge variant="success" className="capitalize">{u.tier}</Badge>
          )}
          {u.prime_grant_active && (
            <Badge variant="info">Free Prime</Badge>
          )}
          {u.disabled && <Badge variant="destructive">Disabled</Badge>}
          {u.locked && (
            <Badge variant="warning">
              Locked{u.lockout_remaining_seconds
                ? ` · ${formatDuration(u.lockout_remaining_seconds)} left`
                : ""}
            </Badge>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="grid grid-cols-2 gap-3">
        <DetailItem label="User ID" value={String(u.id)} />
        <DetailItem label="Sessions" value={String(u.session_count ?? 0)} />
        <DetailItem label="Created" value={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
        <DetailItem label="Last Login" value={u.last_login ? new Date(u.last_login).toLocaleString() : "Never"} />
        {!!u.failed_attempts && (
          <DetailItem
            label="Failed Logins"
            value={
              u.locked && u.lockout_remaining_seconds
                ? `${u.failed_attempts} · locked ${formatDuration(u.lockout_remaining_seconds)}`
                : String(u.failed_attempts)
            }
          />
        )}
      </div>

      {/* Organizations the user belongs to */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold opacity-70 uppercase">Organizations</h3>
        {accessQ.isLoading ? (
          <div className="text-xs opacity-60">Loading…</div>
        ) : accessQ.isError ? (
          <div className="text-xs text-error">Failed to load memberships</div>
        ) : (accessQ.data?.organizations?.length ?? 0) === 0 ? (
          <div className="text-xs opacity-60">Not a member of any organization</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {accessQ.data!.organizations.map((o) => (
              <div key={o.organization_id} className="inline-flex items-center gap-1">
                <Link to={`/organizations/${o.organization_id}`}>
                  <Badge variant="default" className="gap-1 cursor-pointer hover:opacity-80">
                    <Building className="h-3 w-3" />
                    {o.organization_name || `Org ${o.organization_id}`}
                  </Badge>
                </Link>
                <OrgRoleBadge role={o.role as "manager" | "driver"} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Units directly assigned to the user */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold opacity-70 uppercase">
          Assigned Units{accessQ.data ? ` (${accessQ.data.units.length})` : ""}
        </h3>
        {accessQ.isLoading ? (
          <div className="text-xs opacity-60">Loading…</div>
        ) : accessQ.isError ? (
          <div className="text-xs text-error">Failed to load units</div>
        ) : (accessQ.data?.units?.length ?? 0) === 0 ? (
          <div className="text-xs opacity-60">
            No units assigned directly (org managers reach their org's units via
            the organization, not per-unit assignment)
          </div>
        ) : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {accessQ.data!.units.map((unit) => {
              // Same custom-name rule as the dashboard table: the backend
              // default `Unit <id>` is not a real name.
              const hasCustomName = !!unit.name && unit.name !== `Unit ${unit.unit_id}`;
              return (
                <Link
                  key={unit.unit_id}
                  to={`/unit/${encodeURIComponent(unit.unit_id)}`}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/60"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${unit.is_online ? "bg-success" : "bg-base-300"}`}
                    title={unit.is_online ? "online" : "offline"}
                  />
                  {hasCustomName && <span className="truncate font-medium">{unit.name}</span>}
                  <span className="truncate font-mono text-xs opacity-60">{unit.unit_id}</span>
                  {unit.state_name && (
                    <span className="ml-auto shrink-0 text-xs opacity-60">{unit.state_name}</span>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Contact & recovery */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold opacity-70 uppercase">Contact & Recovery</h3>
          {u.email
            ? (u.email_verified
                ? <Badge variant="success" className="badge-sm">Email verified</Badge>
                : <Badge variant="warning" className="badge-sm">Email unverified</Badge>)
            : <Badge variant="destructive" className="badge-sm">No email</Badge>}
        </div>
        {editContact ? (
          <div className="space-y-2">
            <div>
              <div className="text-xs opacity-60 mb-1">Email</div>
              <Input
                type="email"
                autoComplete="off"
                placeholder="name@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            <div>
              <div className="text-xs opacity-60 mb-1">Phone</div>
              <Input
                type="tel"
                autoComplete="off"
                placeholder="+15551234567"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
              />
            </div>
            <div className="text-xs opacity-60">
              An admin-set email is marked verified and can immediately receive a
              reset code. Leave a field empty to clear it.
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => contactM.mutate()} disabled={contactM.isPending}>
                {contactM.isPending ? "Saving..." : "Save contact"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditContact(false);
                  setEmailInput(u.email ?? "");
                  setPhoneInput(u.phone ?? "");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <DetailItem label="Email" value={u.email || "—"} />
              <DetailItem label="Phone" value={u.phone || "—"} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditContact(true)}>
                {u.email || u.phone ? "Edit contact" : "Add contact"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => reset.onTrigger()}
                disabled={reset.disabled}
                title={reset.title}
              >
                {reset.label}
              </Button>
            </div>
          </div>
        )}
      </div>
      {reset.dialog}

      {/* Subscription / Prime */}
      <div className="space-y-2 pt-2 border-t border-border">
        <h3 className="text-sm font-semibold opacity-70 uppercase">Subscription</h3>
        <div className="grid grid-cols-2 gap-3">
          <DetailItem
            label="Current plan"
            value={u.tier ? u.tier.charAt(0).toUpperCase() + u.tier.slice(1) : "Free"}
          />
          <DetailItem
            label="Free Prime until"
            value={
              u.prime_grant_active && u.prime_until
                ? new Date(u.prime_until).toLocaleString()
                : "—"
            }
          />
        </div>
        {u.prime_grant_active && u.base_tier && u.base_tier !== "free" && (
          <div className="text-xs opacity-60">
            Base plan is {u.base_tier}; the free grant does not change it.
          </div>
        )}
        {/* Admins bypass all tier limits, so granting Prime to an admin (or to
            yourself) has no effect — only offer it for non-admin, non-self. */}
        {!isSelf && u.role !== "admin" && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <div className="text-xs opacity-60 mb-1">Days</div>
              <Input
                type="number"
                min={1}
                max={3650}
                className="w-24"
                value={primeDays}
                onChange={(e) => setPrimeDays(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const days = parseInt(primeDays, 10);
                if (!Number.isFinite(days) || days <= 0 || days > 3650) {
                  toast.error("Enter a number of days between 1 and 3650");
                  return;
                }
                grantPrimeM.mutate(days);
              }}
              disabled={grantPrimeM.isPending}
            >
              {u.prime_grant_active ? "Extend / Re-grant Prime" : "Grant free Prime"}
            </Button>
            {u.prime_grant_active && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (confirm(`Revoke free Prime for "${u.username}"? They revert to their base plan immediately.`))
                    revokePrimeM.mutate();
                }}
                disabled={revokePrimeM.isPending}
              >
                Revoke Prime
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Admins do not set or see users' passwords: the reset flow above sends
          the user an email/SMS and the user chooses their own new password.
          The legacy direct "Change Password" form was removed for that reason. */}

      {/* Actions */}
      {!isSelf && (
        <div className="space-y-2 pt-2 border-t border-border">
          <h3 className="text-sm font-semibold opacity-70 uppercase">Actions</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Role changes are high-impact both ways — always confirm.
                const msg =
                  u.role === "admin"
                    ? `Demote admin "${u.username}" to a regular user? They will immediately lose access to all admin pages and fleet-wide data.`
                    : `Promote "${u.username}" to ADMIN? Admins get full access to ALL units, users, billing and settings.`;
                if (confirm(msg)) roleM.mutate();
              }}
              disabled={roleM.isPending}
            >
              {u.role === "admin" ? "Demote to User" : "Promote to Admin"}
            </Button>
            {u.locked && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => unlockM.mutate()}
                disabled={unlockM.isPending}
              >
                Unlock (clear lockout)
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (u.disabled || confirm(`Disable user "${u.username}"? They will be logged out and unable to sign in.`))
                  disableM.mutate();
              }}
              disabled={disableM.isPending}
            >
              {u.disabled ? "Enable Login" : "Disable Login"}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => { if (confirm(`Delete user "${u.username}"?`)) deleteM.mutate(); }}
              disabled={deleteM.isPending}
            >
              Delete User
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card rounded-lg p-3">
      <div className="text-xs opacity-60">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
